import { useEffect, useRef, type HTMLAttributes, type ReactNode } from "react";

import { Button } from "./Button";

export type ModalSize = "confirm" | "compact" | "default" | "large" | "preview";

type ModalAccessibleName =
  | { ariaLabel: string; ariaLabelledBy?: never }
  | { ariaLabel?: never; ariaLabelledBy: string };

export type ModalProps = ModalAccessibleName & {
  children: ReactNode;
  onClose: () => void;
  ariaDescribedBy?: string;
  className?: string;
  dismissible?: boolean;
  role?: "dialog" | "alertdialog";
  size?: ModalSize;
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function classes(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(" ");
}

export function Modal({
  children,
  onClose,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  className,
  dismissible = true,
  role = "dialog",
  size = "default",
}: ModalProps) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  closeRef.current = onClose;
  dismissibleRef.current = dismissible;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    if (!dialog.contains(document.activeElement)) dialog.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissibleRef.current) {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(
        (element) =>
          !element.hasAttribute("disabled") &&
          element.getAttribute("aria-hidden") !== "true",
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return (
    <div
      className={classes("ui-modal-backdrop", `ui-modal-backdrop--${size}`)}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && dismissible) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className={classes("ui-modal", `ui-modal--${size}`, className)}
        role={role}
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
      >
        {children}
      </section>
    </div>
  );
}

export function ModalCloseButton({
  onClick,
  disabled = false,
  floating = false,
  label = "Close",
}: {
  onClick: () => void;
  disabled?: boolean;
  floating?: boolean;
  label?: string;
}) {
  const button = (
    <Button
      className="ui-modal__close"
      tone="quiet"
      size="sm"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      ×
    </Button>
  );
  return floating ? (
    <div className="ui-modal__floating-close">{button}</div>
  ) : (
    button
  );
}

export function ModalHeader({
  title,
  titleId,
  onClose,
  closeDisabled = false,
  closeLabel = "Close",
  eyebrow,
}: {
  title: ReactNode;
  titleId: string;
  onClose: () => void;
  closeDisabled?: boolean;
  closeLabel?: string;
  eyebrow?: ReactNode;
}) {
  return (
    <header className="ui-modal__header">
      <div className="ui-modal__heading">
        {eyebrow != null && (
          <span className="ui-modal__eyebrow">{eyebrow}</span>
        )}
        <h2 id={titleId} className="ui-modal__title">
          {title}
        </h2>
      </div>
      <ModalCloseButton
        onClick={onClose}
        disabled={closeDisabled}
        label={closeLabel}
      />
    </header>
  );
}

export function ModalBody({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={classes("ui-modal__body", className)} {...props} />;
}

export function ModalFooter({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <footer className={classes("ui-modal__footer", className)} {...props} />
  );
}
